import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import Input from '../../../components/Input/Input'
import Dropdown from '../../../components/Dropdown/Dropdown'
import {
  loadAvailableFields,
  loadWebhookConfig
} from '../../../features/workflow/workflowSlice'

const buildEndpoints = (config) => {
  if (Array.isArray(config?.endpoints) && config.endpoints.length > 0) {
    return config.endpoints.map((endpoint) => ({
      webhookUrl: endpoint.webhookUrl || '',
      apiKey: endpoint.apiKey || ''
    }))
  }

  return [{ webhookUrl: config?.webhookUrl || '', apiKey: config?.apiKey || '' }]
}

const ExternalApiConfig = ({ config, onChange, actionId }) => {
  const dispatch = useDispatch()
  const { availableFields: rawAvailableFields, webhookConfig } = useSelector(state => state.workflow)

  const availableFields = Array.isArray(rawAvailableFields)
    ? rawAvailableFields
    : (rawAvailableFields?.fields || [])

  const [endpoints, setEndpoints] = useState(buildEndpoints(config))
  const [selectedFields, setSelectedFields] = useState(config?.selectedFields || [])
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (config && Object.keys(config).length > 0) {
      setEndpoints(buildEndpoints(config))
      setSelectedFields(config.selectedFields || [])
    }
  }, [config])

  useEffect(() => {
    if (!availableFields || availableFields.length === 0) {
      dispatch(loadAvailableFields())
    }
    if (!webhookConfig.webhookUrl) {
      dispatch(loadWebhookConfig())
    }
  }, [dispatch, availableFields, webhookConfig])

  const validateUrl = (url) => {
    if (!url) return 'URL is required'
    try {
      new URL(url)
      return null
    } catch (e) {
      return 'Invalid URL format'
    }
  }

  const validateApiKey = (apiKey) => {
    if (!apiKey) return 'API Key is required'
    if (apiKey.length < 10) return 'API Key must be at least 10 characters'
    return null
  }

  const emit = (nextEndpoints, nextFields) => {
    onChange({ endpoints: nextEndpoints, selectedFields: nextFields })
  }

  const handleEndpointChange = (index, field, value) => {
    const nextEndpoints = endpoints.map((endpoint, i) => (
      i === index ? { ...endpoint, [field]: value } : endpoint
    ))
    setEndpoints(nextEndpoints)

    const error = field === 'webhookUrl' ? validateUrl(value) : validateApiKey(value)
    setErrors(prev => ({ ...prev, [`${field}-${index}`]: error }))

    if (!error) {
      emit(nextEndpoints, selectedFields)
    }
  }

  const handleAddEndpoint = () => {
    const nextEndpoints = [...endpoints, { webhookUrl: '', apiKey: '' }]
    setEndpoints(nextEndpoints)
    emit(nextEndpoints, selectedFields)
  }

  const handleRemoveEndpoint = (index) => {
    if (endpoints.length <= 1) return
    const nextEndpoints = endpoints.filter((_, i) => i !== index)
    setEndpoints(nextEndpoints)
    emit(nextEndpoints, selectedFields)
  }

  const handleFieldsChange = (value) => {
    setSelectedFields(value)
    const error = (!value || value.length === 0) ? 'At least one field must be selected' : null
    setErrors(prev => ({ ...prev, selectedFields: error }))
    if (!error) {
      emit(endpoints, value)
    }
  }

  const fieldMenuItems = availableFields?.map(field => ({
    value: field.key,
    label: field.label
  })) || []

  return (
    <div className="flex flex-col gap-4 p-4 bg-surface rounded-lg border border-divider mt-4">
      <h4 className="text-lg font-semibold text-text-primary">
        External API Configuration
      </h4>

      <div className="flex flex-col gap-4">
        <h5 className="text-md font-medium text-text-secondary">
          Outgoing API Call Settings
        </h5>

        {endpoints.map((endpoint, index) => (
          <div key={index} className="flex flex-col gap-4 p-3 rounded-lg border border-divider">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-text-secondary">
                {`Service ${index + 1}`}
              </span>
              {endpoints.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveEndpoint(index)}
                  className="text-xs text-error-main hover:underline cursor-pointer"
                >
                  Remove
                </button>
              )}
            </div>

            <div>
              <Input
                label="External API URL *"
                placeholder="https://your-api.example.com/webhook"
                value={endpoint.webhookUrl}
                onChange={(e) => handleEndpointChange(index, 'webhookUrl', e.target.value)}
                errorMsg={errors[`webhookUrl-${index}`]}
                type="text"
                required
              />
              <p className="text-xs text-text-secondary mt-1 ml-4">
                The URL where the API call will be sent when the trigger fires
              </p>
            </div>

            <div>
              <Input
                label="API Key (for outgoing call) *"
                placeholder="Enter API key for external service"
                value={endpoint.apiKey}
                onChange={(e) => handleEndpointChange(index, 'apiKey', e.target.value)}
                errorMsg={errors[`apiKey-${index}`]}
                type="password"
                required
              />
              <p className="text-xs text-text-secondary mt-1 ml-4">
                The API key that will be sent in the x-api-key header
              </p>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={handleAddEndpoint}
          className="self-start text-sm text-primary-main hover:underline cursor-pointer"
        >
          + Add service
        </button>

        <div>
          <Dropdown
            label="Payload Fields *"
            placeholder="Select fields to include in API call"
            value={selectedFields || []}
            setValue={handleFieldsChange}
            menuItems={fieldMenuItems}
            isMulti={true}
            className="w-full"
          />
          {errors.selectedFields && (
            <p className="text-xs text-error-main mt-1 ml-4">
              {typeof errors.selectedFields === 'string'
                ? errors.selectedFields
                : 'Invalid field selection'}
            </p>
          )}
          <p className="text-xs text-text-secondary mt-1 ml-4">
            Select which fields to include in the API request payload
          </p>
        </div>
      </div>

      <div className="bg-primary-main/10 border border-primary-main/20 rounded p-3 mt-2">
        <p className="text-xs text-text-secondary">
          <strong>How it works:</strong> When the trigger fires, a POST request is sent to each service URL with the selected fields.
          Add a second service with “+ Add service” to send the same request to two providers at once; the first callback to the CRM wins.
        </p>
      </div>
    </div>
  )
}

ExternalApiConfig.propTypes = {
  config: PropTypes.shape({
    webhookUrl: PropTypes.string,
    apiKey: PropTypes.string,
    endpoints: PropTypes.arrayOf(PropTypes.shape({
      webhookUrl: PropTypes.string,
      apiKey: PropTypes.string
    })),
    selectedFields: PropTypes.arrayOf(PropTypes.string)
  }),
  onChange: PropTypes.func.isRequired,
  actionId: PropTypes.string
}

ExternalApiConfig.defaultProps = {
  config: {
    endpoints: [{ webhookUrl: '', apiKey: '' }],
    selectedFields: []
  }
}

export default ExternalApiConfig
